// announcements-layer/composables/useAnnouncementsUnread.ts
import { computed, type MaybeRefOrGetter } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'

import { useAnnouncementsQueryStore } from '../stores/announcements.query-store'
import { useAnnouncementsLocationId } from './useAnnouncementsLocationId'

/**
 * Unread-count source for the courses banner and group learning nav.
 * Skips the unread-count API when the learner is logged out.
 */
export function useAnnouncementsUnread(locationId?: MaybeRefOrGetter<string | undefined>) {
  const { store: authStore } = useAuth()
  const resolvedLocationId = useAnnouncementsLocationId(locationId)
  const queryStore = useAnnouncementsQueryStore(resolvedLocationId)

  const isLoggedIn = computed(() => authStore.isLoggedIn)

  const unreadQuery = queryStore.fetchUnreadCountQuery(isLoggedIn)

  // Auth gate on the count is product behavior (logged-out learners see 0), not a
  // TanStack status facade — status refs (isLoading) stay as query refs.
  const unreadCount = computed(() => (isLoggedIn.value ? (unreadQuery.data.value ?? 0) : 0))

  return {
    unreadQuery,
    unreadCount,
    hasUnread: computed(() => isLoggedIn.value && unreadCount.value > 0),
    isLoggedIn,
    isLoading: unreadQuery.isPending,
    refetch: unreadQuery.refetch,
  }
}
